import { useState, useCallback } from 'react';
import { deduplicated } from '~/util/array';

type Key = string | number;

type SelectAllFunction<T> = (items: Array<T>, setSelected: boolean) => void;
type SelectFunction<T> = (value: T) => void;

export type ReturnProps<T = string> = {
  itemsSelected: Array<T>;
  allSelected: boolean;

  /**
   * Adds/removes selection for the provided page items.
   * - when `setSelected` is `true`: merges `items` into current selection (deduplicated)
   * - when `setSelected` is `false`: removes `items` from current selection
   *
   * Note: this will disable `allSelected` because page/manual changes invalidate "select all (across pages)".
   */
  selectAllItemsOnPage: SelectAllFunction<T>;

  /**
   * Toggles selection for a single row.
   *
   * Note: this will disable `allSelected` because manual changes invalidate "select all (across pages)".
   */
  toggleRowSelect: SelectFunction<T>;

  /** Returns `true` if the given row is selected. */
  checkIfSelected: (value: T) => boolean;

  /**
   * Toggles "select all (across pages)" mode using the provided `items`.
   * - if `allSelected` is `false`: selects (deduplicated) + sets `allSelected` to `true`
   * - if `allSelected` is `true`: clears selection + sets `allSelected` to `false`
   */
  selectAllItems: (items: Array<T>) => void;

  /** Clears selection and disables `allSelected`. */
  deselectAllItems: () => void;
};

type UseRowSelect = {
  /**
   * Use this overload when rows are primitives (string/number ids).
   *
   * Example:
   *   const rowSelect = useRowSelect<string>();
   */
  <T extends Key>(): ReturnProps<T>;

  /**
   * Use this overload when rows are objects (or anything not string/number).
   * You MUST provide a stable unique key extractor.
   *
   * Example:
   *   type Row = { id: string; name: string };
   *   const rowSelect = useRowSelect<Row>(row => row.id);
   */
  <T>(getKey: (item: T) => Key): ReturnProps<T>;
};

/**
 * A hook to manage row selection in a table.
 *
 * It returns:
 * - `itemsSelected` - currently selected rows
 * - `allSelected` - whether "select all (across pages)" mode is enabled
 * - helpers to toggle/select/deselect rows
 *
 * Important:
 * - If your row type is an object, you MUST pass `getKey` to avoid selection bugs
 *   (object reference equality is unstable across renders/pages).
 */
const useRowSelect: UseRowSelect = <T,>(getKey?: (item: T) => Key) => {
  const [itemsSelected, setItemsSelected] = useState<Array<T>>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const getItemKey = useCallback(
    (item: T): Key => {
      if (getKey) return getKey(item);
      return item as unknown as Key;
    },
    [getKey],
  );

  const checkIfSelected = useCallback(
    (value: T): boolean => {
      const valueKey = getItemKey(value);
      return itemsSelected.some(item => getItemKey(item) === valueKey);
    },
    [itemsSelected, getItemKey],
  );

  const toggleRowSelect = useCallback(
    (value: T): void => {
      const valueKey = getItemKey(value);

      setAllSelected(false);

      setItemsSelected(prev => {
        const exists = prev.some(item => getItemKey(item) === valueKey);
        if (exists) return prev.filter(item => getItemKey(item) !== valueKey);
        return [...prev, value];
      });
    },
    [getItemKey],
  );

  const selectAllItemsOnPage = useCallback(
    (items: Array<T>, shouldBeSelected: boolean): void => {
      setAllSelected(false);

      if (shouldBeSelected) {
        setItemsSelected(prev => deduplicated([...prev, ...items], getItemKey));
        return;
      }

      const itemKeys = new Set(items.map(item => getItemKey(item)));
      setItemsSelected(prev =>
        prev.filter(item => !itemKeys.has(getItemKey(item))),
      );
    },
    [getItemKey],
  );

  const selectAllItems = useCallback(
    (items: Array<T>): void => {
      if (allSelected) {
        setItemsSelected([]);
        setAllSelected(false);
        return;
      }

      setItemsSelected(prev => deduplicated([...prev, ...items], getItemKey));
      setAllSelected(true);
    },
    [allSelected, getItemKey],
  );

  const deselectAllItems = useCallback((): void => {
    setItemsSelected([]);
    setAllSelected(false);
  }, []);

  return {
    itemsSelected,
    allSelected,
    selectAllItemsOnPage,
    toggleRowSelect,
    checkIfSelected,
    selectAllItems,
    deselectAllItems,
  };
};

export default useRowSelect;
